import * as cmState from "@codemirror/state";
import * as cmView from "@codemirror/view";
import * as yorkie from "@yorkie-js/sdk";

export type YorkieDocType = {
	content: yorkie.Text<yorkie.Indexable>;
};

export type YorkiePresenceType = {
	color: string;
	name: string;
	selection: yorkie.TextPosStructRange | null;
	cursor: [number, number] | null;
};

export class YorkieSyncConfig<T extends YorkieDocType, P extends YorkiePresenceType> {
	doc: yorkie.Document<T, P>;
	client: yorkie.Client;
	constructor(doc: yorkie.Document<T, P>, client: yorkie.Client) {
		this.doc = doc;
		this.client = client;
	}
}

export const yorkieSyncFacet: cmState.Facet<
	YorkieSyncConfig<YorkieDocType, YorkiePresenceType>,
	YorkieSyncConfig<YorkieDocType, YorkiePresenceType>
> = cmState.Facet.define({
	combine(inputs) {
		return inputs[inputs.length - 1];
	},
});

export const yorkieSyncAnnotation: cmState.AnnotationType<
	YorkieSyncConfig<YorkieDocType, YorkiePresenceType>
> = cmState.Annotation.define();

class YorkieSyncPluginValue implements cmView.PluginValue {
	view: cmView.EditorView;
	conf: YorkieSyncConfig<YorkieDocType, YorkiePresenceType>;
	_doc: yorkie.Document<YorkieDocType, YorkiePresenceType>;

	constructor(view: cmView.EditorView) {
		this.view = view;
		this.conf = view.state.facet(yorkieSyncFacet);
		this._doc = this.conf.doc;

		this._doc.subscribe((event) => {
			if (event.type !== "snapshot") return;

			// The text is replaced to snapshot and must be re-synced.
			const text = this._doc.getRoot().content;
			const selection = this._doc.getMyPresence().selection;
			const transactionSpec: cmState.TransactionSpec = {
				changes: { from: 0, to: view.state.doc.length, insert: text.toString() },
				annotations: [cmState.Transaction.remote.of(true)],
			};

			if (selection) {
				// Restore the cursor position when the text is replaced.
				const cursor = text.posRangeToIndexRange(selection);
				transactionSpec["selection"] = {
					anchor: cursor[0],
					head: cursor[1],
				};
			}

			view.dispatch(transactionSpec);
		});

		this._doc.update((root) => {
			if (root.content) return;
			root.content = new yorkie.Text();
		});

		this._doc.subscribe((event) => {
			if (
				event.type !== "remote-change" &&
				!(event.type === "local-change" && event.source === "undoredo")
			) {
				return;
			}

			const isUndoRedo = event.type === "local-change" && event.source === "undoredo";
			const { operations } = event.value;

			// Check if content itself is replaced
			const contentReplaced = operations.some(
				(op) => op.type === "remove" && op.path === "$"
			);

			if (contentReplaced) {
				// Content object is replaced, reload entire document
				const text = this._doc.getRoot().content;
				view.dispatch({
					changes: { from: 0, to: view.state.doc.length, insert: text.toString() },
					annotations: [cmState.Transaction.remote.of(true)],
				});
				return;
			}

			// Handle individual edit operations
			operations.forEach((op) => {
				if (op.type === "edit" && op.path?.startsWith("$.content")) {
					const changes = [
						{
							from: Math.max(0, op.from),
							to: Math.max(0, op.to),
							insert: op.value!.content,
						},
					];
					const transactionSpec: cmState.TransactionSpec = {
						changes,
						annotations: [cmState.Transaction.remote.of(true)],
					};

					if (isUndoRedo) {
						const newPos = op.from + (op.value?.content?.length || 0);
						const docLength =
							view.state.doc.length +
							((op.value?.content?.length || 0) - (op.to - op.from));
						const boundedPos = Math.min(Math.max(0, newPos), docLength);

						transactionSpec.selection = {
							anchor: boundedPos,
							head: boundedPos,
						};
					}

					view.dispatch(transactionSpec);
				}
			});
		});
	}

	update(update: cmView.ViewUpdate) {
		if (update.docChanged) {
			for (const tr of update.transactions) {
				const events = ["select", "input", "delete", "move", "undo", "redo"];
				if (!events.some((event) => tr.isUserEvent(event))) {
					continue;
				}
				if (tr.annotation(cmState.Transaction.remote)) {
					continue;
				}
				let adj = 0;
				this._doc.update((root) => {
					tr.changes.iterChanges((fromA, toA, _, __, inserted) => {
						const insertText = inserted.toJSON().join("\n");
						root.content.edit(fromA + adj, toA + adj, insertText);
						adj += insertText.length - (toA - fromA);
					});
				});
			}
		}
	}
}

export const yorkieSync = cmView.ViewPlugin.fromClass(YorkieSyncPluginValue);
